import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { axiosGet } from '../utils/api';
import { FaArrowRight, FaSearch } from 'react-icons/fa';
import axios from 'axios';
import { storageManager } from '../utils/storageManager';
import { quranDataKey } from '../utils/quranCacheKeys';
import { useAudioContext } from '../contexts/AudioContext';
import {
  resolveReadingPosition,
  resolveActiveJuz,
} from '../utils/readingPosition';
import { getReadingPositionState, subscribeReadingPosition } from '../utils/readingPositionStore';

const TABS = [
  { id: 'surah', label: 'Surah' },
  { id: 'verse', label: 'Verse' },
  { id: 'juz', label: 'Juz' },
  { id: 'page', label: 'Page' },
];

const TOTAL_MUSHAF_PAGES = 604;

const useFetchSurahs = () => {
  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSurahs = async () => {
      const surahsKey = quranDataKey('surahs');
      const cachedSurahs = storageManager.getItem(surahsKey);
      if (cachedSurahs) {
        try {
          setSurahs(cachedSurahs);
          setLoading(false);
          return;
        } catch (e) {
          console.error('Failed to parse cached surahs:', e);
          storageManager.removeItem(surahsKey);
        }
      }

      setLoading(true);
      try {
        const { surahs: list } = await axiosGet('/surahs');
        setSurahs(list);
        storageManager.setItem(surahsKey, list);
        setError(null);
      } catch (err) {
        console.error('Error fetching Surahs:', err);
        setError('Failed to load Surahs. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchSurahs();
  }, []);

  return { surahs, loading, error };
};

const useFetchAyahs = (selectedSurah) => {
  const [ayahs, setAyahs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAyahs([]);
    if (!selectedSurah) {
      setLoading(false);
      return undefined;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchAyahs = async () => {
      const cacheKey = quranDataKey(`ayahs_${selectedSurah.surah_id}`);
      const cachedAyahs = storageManager.getItem(cacheKey);

      if (cachedAyahs) {
        try {
          setAyahs(cachedAyahs);
          setLoading(false);
          return;
        } catch (e) {
          console.error(`Failed to parse cached ayahs for surah ${selectedSurah.surah_id}:`, e);
          storageManager.removeItem(cacheKey);
        }
      }

      setLoading(true);
      try {
        const { ayahs: list } = await axiosGet(`/surah/${selectedSurah.surah_id}`, {}, signal);
        setAyahs(list);
        storageManager.setItem(cacheKey, list);
      } catch (error) {
        if (error.name !== 'AbortError' && !axios.isCancel(error)) {
          console.error('Error fetching Ayahs:', error);
        }
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchAyahs();
    return () => abortController.abort();
  }, [selectedSurah]);

  return { ayahs, loading };
};

const useFetchParahs = (enabled) => {
  const [parahs, setParahs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const load = async () => {
      const parahsKey = quranDataKey('parahsData');
      const cached = storageManager.getItem(parahsKey);
      if (cached) {
        setParahs(cached);
        return;
      }
      setLoading(true);
      try {
        const response = await axiosGet('/parahs');
        if (!cancelled) {
          setParahs(response.parahs || []);
          storageManager.setItem(parahsKey, response.parahs || []);
        }
      } catch (e) {
        console.error('Error fetching parahs:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { parahs, loading };
};

function DirectJump({
  isCollapsed = false,
  setIsCollapsed,
  onNavigate,
  variant = 'panel',
  currentAyahId: currentAyahIdProp = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { surahId: routeSurahId } = useParams();
  const { currentlyPlayingAyah } = useAudioContext();
  const [storedAyahId, setStoredAyahId] = useState(
    () => getReadingPositionState().currentAyahId
  );

  useEffect(() => {
    return subscribeReadingPosition((s) => {
      setStoredAyahId(s.currentAyahId);
    });
  }, []);

  const { surahs, loading: surahsLoading, error: surahsError } = useFetchSurahs();
  const [tab, setTab] = useState('surah');
  const [selected, setSelected] = useState({ surah: null, ayah: null });
  const [activeJuzNumber, setActiveJuzNumber] = useState(null);
  const [surahQuery, setSurahQuery] = useState('');
  const [verseQuery, setVerseQuery] = useState('');
  const [pageQuery, setPageQuery] = useState('');
  const [selectedPage, setSelectedPage] = useState('');
  const { ayahs, loading: ayahsLoading } = useFetchAyahs(selected.surah);
  const isDrawer = variant === 'drawer';
  const isOpen = isDrawer || !isCollapsed;
  const { parahs, loading: parahsLoading } = useFetchParahs(
    tab === 'juz' || isOpen
  );
  const containerRef = useRef(null);
  const surahListRef = useRef(null);
  const verseGridRef = useRef(null);
  const juzListRef = useRef(null);
  const userTouchedRef = useRef(false);

  const readingPath = location.pathname.startsWith('/arabicreading') ? 'arabicreading' : 'surah';

  const closeAfterJump = useCallback(() => {
    if (typeof onNavigate === 'function') onNavigate();
    else if (typeof setIsCollapsed === 'function') setIsCollapsed(false);
  }, [onNavigate, setIsCollapsed]);

  const syncSelection = useCallback(() => {
    if (!surahs.length) return;

    const pos = resolveReadingPosition({
      playingAyahKey: currentlyPlayingAyah?.ayahKey,
      currentAyahId: currentAyahIdProp,
      storedAyahId,
      routeSurahId,
    });

    if (!pos.surahId) return;

    const match = surahs.find((s) => s.surah_id === pos.surahId) || null;
    setSelected({
      surah: match,
      ayah: pos.ayahId || (match ? `${match.surah_id}:1` : null),
    });

    if (parahs.length && pos.ayahId) {
      const juz = resolveActiveJuz(parahs, pos.ayahId);
      setActiveJuzNumber(juz?.parah_number ?? null);
    }

    // Prefill page from mushaf route state when available
    const pageFromState = location.state?.pageNumber;
    if (pageFromState) {
      setPageQuery(String(pageFromState));
      setSelectedPage(String(pageFromState));
    }
  }, [
    surahs,
    currentlyPlayingAyah?.ayahKey,
    currentAyahIdProp,
    storedAyahId,
    routeSurahId,
    parahs,
    location.state?.pageNumber,
  ]);

  // When Go To opens (sidebar or top panel), select current surah + ayah
  useEffect(() => {
    if (!isOpen) {
      userTouchedRef.current = false;
      return;
    }
    userTouchedRef.current = false;
    syncSelection();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- sync once on open

  // Keep in sync with scroll / playback while open (unless user changed picks)
  useEffect(() => {
    if (!isOpen || userTouchedRef.current) return;
    syncSelection();
  }, [
    isOpen,
    currentlyPlayingAyah?.ayahKey,
    currentAyahIdProp,
    storedAyahId,
    surahs,
    parahs,
    syncSelection,
  ]);

  // Scroll lists to the selected items
  useEffect(() => {
    if (!isOpen) return;
    const sid = selected.surah?.surah_id;
    if (tab === 'surah' && sid) {
      surahListRef.current
        ?.querySelector(`[data-jump-surah="${sid}"]`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    if (tab === 'verse' && selected.ayah) {
      const n = String(selected.ayah).split(':')[1];
      verseGridRef.current
        ?.querySelector(`[data-jump-verse="${n}"]`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    if (tab === 'juz' && activeJuzNumber) {
      juzListRef.current
        ?.querySelector(`[data-jump-juz="${activeJuzNumber}"]`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isOpen, tab, selected.surah?.surah_id, selected.ayah, activeJuzNumber]);

  const filteredSurahs = useMemo(() => {
    const q = surahQuery.trim().toLowerCase();
    if (!q) return surahs;
    return surahs.filter((s) => {
      const id = String(s.surah_id);
      const en = (s.name_english || '').toLowerCase();
      const ar = (s.name_arabic || '').toLowerCase();
      const gu = (s.name_gujarati || '').toLowerCase();
      return id.includes(q) || en.includes(q) || ar.includes(q) || gu.includes(q);
    });
  }, [surahs, surahQuery]);

  const selectedAyahNumber = useMemo(() => {
    if (!selected.ayah) return null;
    const n = Number(String(selected.ayah).split(':')[1]);
    return Number.isFinite(n) ? n : null;
  }, [selected.ayah]);

  const verseNumbers = useMemo(() => {
    const total = selected.surah?.total_ayahs || ayahs.length || 0;
    if (!total) return [];
    const all = Array.from({ length: total }, (_, i) => i + 1);
    const q = verseQuery.replace(/\D/g, '');
    if (!q) return all;
    return all.filter((n) => String(n).startsWith(q));
  }, [selected.surah, ayahs.length, verseQuery]);

  const jumpToAyah = useCallback(
    (surahId, ayahIdOrNumber) => {
      const ayahId =
        typeof ayahIdOrNumber === 'string' && ayahIdOrNumber.includes(':')
          ? ayahIdOrNumber
          : `${surahId}:${ayahIdOrNumber}`;

      navigate(`/${readingPath}/${surahId}#ayah-${ayahId}`, {
        state: { scrollToAyah: String(ayahId), from: 'DirectJump', at: Date.now() },
      });
      closeAfterJump();
    },
    [navigate, readingPath, closeAfterJump]
  );

  const markTouched = () => {
    userTouchedRef.current = true;
  };

  const handleSurahPick = (surah) => {
    markTouched();
    jumpToAyah(surah.surah_id, `${surah.surah_id}:1`);
  };

  const handleVersePick = (ayahNumber) => {
    if (!selected.surah) return;
    markTouched();
    const ayah = ayahs.find((a) => a.ayah_number === ayahNumber) || null;
    const ayahId = ayah?.ayah_id || `${selected.surah.surah_id}:${ayahNumber}`;
    jumpToAyah(selected.surah.surah_id, ayahId);
  };

  const handleJuzPick = (parah) => {
    markTouched();
    const [sid] = String(parah.ayah_id).split(':');
    jumpToAyah(Number(sid), parah.ayah_id);
  };

  const handlePageGo = () => {
    const page = parseInt(selectedPage || pageQuery, 10);
    if (!Number.isFinite(page) || page < 1 || page > TOTAL_MUSHAF_PAGES) return;
    markTouched();
    navigate('/13linepage', { state: { pageNumber: page } });
    closeAfterJump();
  };

  const handleGoClassic = () => {
    if (!selected.surah || !selected.ayah) return;
    markTouched();
    jumpToAyah(selected.surah.surah_id, selected.ayah);
  };

  const handleClickOutside = useCallback(
    (event) => {
      if (isDrawer) return;
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (setIsCollapsed) setIsCollapsed(false);
      }
    },
    [setIsCollapsed, isDrawer]
  );

  useEffect(() => {
    if (isDrawer || isCollapsed) {
      document.removeEventListener('mousedown', handleClickOutside);
      return undefined;
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCollapsed, handleClickOutside, isDrawer]);

  const shellCls = isDrawer
    ? 'bg-gray-800 text-white p-3 rounded-2xl border border-gray-700'
    : 'mb-4 bg-white dark:bg-gray-800 p-4 rounded-3xl shadow-sm border border-gray-200 dark:border-gray-700';

  const tabBarCls = isDrawer
    ? 'flex gap-1 mb-3 p-1 bg-gray-900 rounded-xl'
    : 'flex gap-1 mb-4 p-1 bg-gray-100 dark:bg-gray-700/60 rounded-2xl';

  const tabBtn = (active) =>
    isDrawer
      ? `flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
          active ? 'bg-teal-600 text-white' : 'text-gray-300 hover:bg-gray-700'
        }`
      : `flex-1 py-2 text-sm font-medium rounded-xl transition-colors ${
          active
            ? 'bg-teal-600 text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-600'
        }`;

  const inputCls = isDrawer
    ? 'w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-xl text-white text-sm focus:ring-1 focus:ring-teal-500'
    : 'w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-2xl text-sm focus:ring-1 focus:ring-teal-500';

  const listCls = isDrawer
    ? 'max-h-52 overflow-y-auto divide-y divide-gray-700 rounded-xl border border-gray-600'
    : 'max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600';

  const selectedRowCls = isDrawer
    ? 'bg-teal-700/40 ring-1 ring-teal-500'
    : 'bg-teal-50 dark:bg-teal-900/40 ring-1 ring-teal-500';

  return (
    <div ref={containerRef} className={shellCls}>
      <div className={tabBarCls}>
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={tabBtn(tab === t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'surah' && (
        <div className="space-y-3">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="search"
              value={surahQuery}
              onChange={(e) => {
                markTouched();
                setSurahQuery(e.target.value);
              }}
              placeholder="Search surah…"
              className={`${inputCls} pl-9`}
            />
          </div>
          {surahsError && <p className="text-xs text-red-400">{surahsError}</p>}
          <div ref={surahListRef} className={listCls}>
            {surahsLoading ? (
              <p className="p-3 text-sm text-gray-400">Loading surahs…</p>
            ) : (
              filteredSurahs.map((surah) => (
                <button
                  key={surah.surah_id}
                  type="button"
                  data-jump-surah={surah.surah_id}
                  onClick={() => handleSurahPick(surah)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    isDrawer ? 'hover:bg-gray-700' : 'hover:bg-teal-50 dark:hover:bg-teal-900/30'
                  } ${selected.surah?.surah_id === surah.surah_id ? selectedRowCls : ''}`}
                >
                  <span className="font-medium text-teal-400 mr-2">{surah.surah_number}.</span>
                  {surah.name_english}
                  <span className="text-gray-400 text-xs ml-2">({surah.total_ayahs})</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {tab === 'verse' && (
        <div className="space-y-3">
          <select
            className={inputCls}
            value={selected.surah?.surah_id || ''}
            onChange={(e) => {
              markTouched();
              const id = parseInt(e.target.value, 10);
              const next = surahs.find((s) => s.surah_id === id) || null;
              setSelected({
                surah: next,
                ayah: next ? `${next.surah_id}:1` : null,
              });
            }}
            disabled={surahsLoading}
          >
            <option value="" disabled>
              Select Surah
            </option>
            {surahs.map((surah) => (
              <option key={surah.surah_id} value={surah.surah_id}>
                {surah.surah_number}. {surah.name_english}
              </option>
            ))}
          </select>

          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="search"
              inputMode="numeric"
              value={verseQuery}
              onChange={(e) => {
                markTouched();
                setVerseQuery(e.target.value);
              }}
              placeholder="Filter verse…"
              disabled={!selected.surah}
              className={`${inputCls} pl-9 disabled:opacity-50`}
            />
          </div>

          <div ref={verseGridRef} className="max-h-52 overflow-y-auto grid grid-cols-4 gap-1.5 p-1">
            {!selected.surah ? (
              <p className="col-span-full text-sm text-gray-400">Select a surah first</p>
            ) : (
              verseNumbers.map((n) => {
                const isActive = selectedAyahNumber === n;
                return (
                  <button
                    key={n}
                    type="button"
                    data-jump-verse={n}
                    onClick={() => handleVersePick(n)}
                    className={`py-2 rounded-xl text-sm border transition-colors ${
                      isActive
                        ? 'bg-teal-600 text-white border-teal-600'
                        : isDrawer
                          ? 'border-gray-600 hover:bg-teal-600'
                          : 'border-gray-200 dark:border-gray-600 hover:bg-teal-600 hover:text-white'
                    }`}
                  >
                    {n}
                  </button>
                );
              })
            )}
          </div>

          <div className={`flex gap-2 items-end pt-1 border-t ${isDrawer ? 'border-gray-700' : 'border-gray-100 dark:border-gray-700'}`}>
            <select
              id="direct-jump-ayah"
              className={`flex-1 ${inputCls}`}
              value={selected.ayah || ''}
              onChange={(e) => {
                markTouched();
                setSelected((prev) => ({ ...prev, ayah: e.target.value }));
              }}
              disabled={ayahsLoading || !selected.surah}
            >
              <option value="" disabled>
                Select Ayah
              </option>
              {ayahs.map((ayah) => (
                <option key={ayah.ayah_id} value={ayah.ayah_id}>
                  Ayah {ayah.ayah_number}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleGoClassic}
              disabled={!selected.surah || !selected.ayah}
              className="flex items-center gap-2 py-2 px-4 bg-teal-600 hover:bg-teal-700 text-white rounded-xl disabled:opacity-50"
            >
              Go <FaArrowRight size={12} />
            </button>
          </div>
        </div>
      )}

      {tab === 'juz' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-400">Jump to Juz start</p>
          <div ref={juzListRef} className={listCls}>
            {parahsLoading && !parahs.length ? (
              <p className="p-3 text-sm text-gray-400">Loading Juz…</p>
            ) : (
              parahs.map((parah) => {
                const preview = (parah.arabic_text || '').split(' ').slice(0, 3).join(' ');
                const isActive = activeJuzNumber === parah.parah_number;
                return (
                  <button
                    key={parah.parah_number}
                    type="button"
                    data-jump-juz={parah.parah_number}
                    onClick={() => handleJuzPick(parah)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left ${
                      isActive
                        ? selectedRowCls
                        : isDrawer
                          ? 'hover:bg-gray-700'
                          : 'hover:bg-teal-50 dark:hover:bg-teal-900/30'
                    }`}
                  >
                    <span className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-teal-500 text-white text-xs font-bold flex items-center justify-center">
                      {parah.parah_number}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium">Juz {parah.parah_number}</span>
                      <span className="block text-xs text-gray-400">{parah.ayah_id}</span>
                    </span>
                    <span className="font-arabic text-sm text-gray-300 truncate max-w-[35%]" dir="rtl">
                      {preview}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {tab === 'page' && (
        <div className="space-y-3">
          <p className="text-xs text-gray-400">13-line page (1–{TOTAL_MUSHAF_PAGES})</p>
          <div className="flex gap-2">
            <input
              type="number"
              min={1}
              max={TOTAL_MUSHAF_PAGES}
              value={pageQuery}
              onChange={(e) => {
                markTouched();
                setPageQuery(e.target.value);
                setSelectedPage(e.target.value);
              }}
              placeholder="Page"
              className={`flex-1 ${inputCls}`}
            />
            <button
              type="button"
              onClick={handlePageGo}
              disabled={!selectedPage || Number(selectedPage) < 1 || Number(selectedPage) > TOTAL_MUSHAF_PAGES}
              className="flex items-center gap-2 py-2 px-4 bg-teal-600 hover:bg-teal-700 text-white rounded-xl disabled:opacity-50"
            >
              Go <FaArrowRight size={12} />
            </button>
          </div>
          <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto">
            {[1, 2, 50, 100, 200, 300, 400, 500, 550, 600, 604].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  markTouched();
                  setPageQuery(String(p));
                  setSelectedPage(String(p));
                  navigate('/13linepage', { state: { pageNumber: p } });
                  closeAfterJump();
                }}
                className={`py-2 rounded-xl text-sm border ${
                  Number(selectedPage) === p
                    ? 'bg-teal-600 text-white border-teal-600'
                    : isDrawer
                      ? 'border-gray-600 hover:bg-teal-600'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-teal-600 hover:text-white'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

DirectJump.propTypes = {
  isCollapsed: PropTypes.bool,
  setIsCollapsed: PropTypes.func,
  onNavigate: PropTypes.func,
  variant: PropTypes.oneOf(['panel', 'drawer']),
  currentAyahId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DirectJump;
